import React from 'react';

import { classNames } from '../dom';
import { CloseButton } from '../Button';
import Alert from '../Alert';
import { Slide, toast as renderToast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import './Toast.scss';

export interface ToastProps {
  /** Classname to pass to toast container */
  className?: string;
  /** Duration in ms that toast will remain on screen */
  duration?: number;
  /** Whether to add a close button to the toast */
  isCloseable?: boolean;
  /** custom id to prevent duplicates */
  toastId?: string;
  /** Default styling for a specific type of toast */
  type: 'success' | 'error' | 'info' | 'warning';
  /** Callback on toast opening */
  onOpen?: () => void;
  /** Callback on toast closing */
  onClose?: () => void;
}

const alertToast = ({ className, type, message }: any) => {
  let level = type;
  if (type === 'error') level = 'danger';
  if (type === 'warn') level = 'warning';

  return (
    <Alert
      className={classNames('react-toast-pxl', className)}
      close={false}
      level={level}
      message={message}
    />
  );
}

/** Actual toast emitter */
const toast = (message: React.ReactNode, props: ToastProps) => {
  const { className, duration = 3000, isCloseable = true, toastId, type, onOpen, onClose } = props;
  if (!type) {
    throw new Error('Type must be defined');
  }

  return renderToast(
    alertToast({
      className,
      message,
      type
    }),
    {
      autoClose: duration,
      className: classNames('ps-toast', className),
      closeButton: isCloseable
        ? ({ closeToast }) => <CloseButton onClick={closeToast} className="ps-toast__close-button" />
        : false,
      closeOnClick: !isCloseable,
      onClose,
      onOpen,
      position: 'top-center',
      toastId,
      type
    }
  );
};

/* Toast initializer. The configure call only needs to happen once. Call this like so -> const toast = useToast();
Then you're free to use toast() wherever to generate a toast **/
export const useToast = () => {
  renderToast.configure({
    className: 'alert-toast-container',
    draggable: false,
    limit: 3,
    hideProgressBar: true,
    newestOnTop: false,
    pauseOnHover: true,
    rtl: false,
    transition: Slide
  });
  return toast;
};

/** Function to dismiss all toasts currently on the page */
export const dismiss = () => renderToast.dismiss();
